import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import sidemenucss from "../../css/sidemenu.module.css";
import commoncss from "../../css/common.module.css";

/**
 * PreviewSidebar
 *
 * Behavior:
 * - BS / PL are rendered from templateData.grouping (special handling).
 * - All other menus (generalNotes, BSNotes, PLNotes, cashflow, socie, disclosure, etc.)
 *   are treated generically and may have nested subNotes.
 * - When switching MAIN MENU (clicking the left column item),
 *   we auto-select the first TOP-LEVEL item only (do NOT auto-select its nested subNote).
 * - Clicking a top-level item selects that top-level item (subSubIndex = null).
 * - Clicking a nested subNote selects that subNote (and stops propagation to parent).
 */

const PreviewSidebar = ({
  menuItems,
  title,
  activeItem,
  showData,
  templateData,
  setSelCat,
}) => {
  const [openSubmenu, setOpenSubmenu] = useState(activeItem ?? 0);

  // Track active indices: menuIndex = main menu, subIndex = top-level item index,
  // subSubIndex = nested subNote index (null if top-level selected)
  const [activeSubmenu, setActiveSubmenu] = useState({
    menuIndex: activeItem ?? 0,
    subIndex: 0,
    subSubIndex: null,
  });

  const routerHistory = useHistory();

  const getNoteKeyByMenuIndex = (index) => {
    switch (index) {
      case 0:
        return "BS";
      case 1:
        return "PL";
      case 2:
        return "generalNotes";
      case 3:
        return "BSNotes";
      case 4:
        return "PLNotes";
      case 5:
        return "cashflow";
      case 6:
        return "socie";
      case 7:
        return "disclosure";
      default:
        return null;
    }
  };

  /******************************************
   * AUTO SELECT FIRST ITEM PER MAIN MENU
   *
   * Important: When switching main menu, ALWAYS select only the TOP-LEVEL item.
   * (Do NOT auto-select nested subNote.)
   ******************************************/
  useEffect(() => {
    if (!templateData) return;

    const noteKey = getNoteKeyByMenuIndex(openSubmenu);
    if (!noteKey) return;

    // BS & PL special handling (grouping)
    if (noteKey === "BS" || noteKey === "PL") {
      const cat = noteKey;
      const list =
        templateData?.grouping
          ?.map((note, idx) => ({ note, idx }))
          .filter(({ note }) => note.category === cat) || [];

      if (list.length > 0) {
        const firstIdx = list[0].idx;
        setActiveSubmenu({
          menuIndex: openSubmenu,
          subIndex: firstIdx,
          subSubIndex: null,
        });
        showData(noteKey, firstIdx, null);
        setSelCat(noteKey);
        return;
      }

      // fallback if no grouping entries
      setActiveSubmenu({ menuIndex: openSubmenu, subIndex: 0, subSubIndex: null });
      showData(noteKey, 0, null);
      setSelCat(noteKey);
      return;
    }

    // GENERIC MENUS (with possible subNotes)
    const arr = templateData?.[noteKey] || [];

    if (arr.length > 0) {
      const firstTop = 0;

      // NEW: Always select the top-level item only when switching main menu.
      setActiveSubmenu({
        menuIndex: openSubmenu,
        subIndex: firstTop,
        subSubIndex: null, // <-- important: do NOT auto-select nested subNote
      });

      // Load top-level item's data
      showData(noteKey, firstTop, null);
      setSelCat(noteKey);
      return;
    }

    // fallback for empty arrays
    setActiveSubmenu({ menuIndex: openSubmenu, subIndex: 0, subSubIndex: null });
    showData(noteKey, 0, null);
    setSelCat(noteKey);
  }, [openSubmenu, templateData]);

  return (
    <div className={`${sidemenucss.sidemenuContainer} ${sidemenucss.sidemenuBackgroundClr} w-100`}>
      <div className={sidemenucss.sidemenuHeader} style={{ minHeight: "15vh" }}>
        <span className={sidemenucss.sidemenuTitle}>{title}</span>
        <span className={sidemenucss.wsName}>workspace name</span>
      </div>

      <div
        className={`${sidemenucss.sidemenuContent} 
                    ${sidemenucss.sidemenuLinksFontSize} 
                    ${sidemenucss.sidemenuContentTop} 
                    ${commoncss.hideScroll} pt-0`}
      >
        <ul className={sidemenucss.sidemenuList}>
          {menuItems?.map((menu, mIdx) => {
            const noteKey = getNoteKeyByMenuIndex(mIdx);

            return (
              <div key={mIdx}>
                {/* MAIN MENU ITEM */}
                <li
                  onClick={() => {
                    setOpenSubmenu(mIdx);
                    // setSelCat expects a key/string — use noteKey
                    setSelCat(noteKey);
                  }}
                  className={`${sidemenucss.sidemenuItem} ${
                    openSubmenu === mIdx ? sidemenucss.sidemenuItemActive : ""
                  }`}
                >
                  <span className={sidemenucss.sidemenuItemname}>
                    <div className={sidemenucss.sideMenuItemContainer}>
                      {menu.label}
                    </div>
                  </span>
                </li>

                {/**********************************
                 * BALANCE SHEET (from grouping)
                 **********************************/}
                {noteKey === "BS" && openSubmenu === mIdx && (
                  <div className={sidemenucss.submenuContainer}>
                    {templateData?.grouping
                      ?.map((note, idx) => ({ note, idx }))
                      .filter(({ note }) => note.category === "BS")
                      .map(({ note, idx }) => (
                        <div
                          key={idx}
                          className={`${sidemenucss.submenuItemNotes} ${
                            activeSubmenu.menuIndex === mIdx &&
                            activeSubmenu.subIndex === idx
                              ? sidemenucss.activeSubMenu
                              : ""
                          }`}
                          onClick={() => {
                            setActiveSubmenu({
                              menuIndex: mIdx,
                              subIndex: idx,
                              subSubIndex: null,
                            });
                            showData("BS", idx, null);
                          }}
                        >
                          {note.name}
                        </div>
                      ))}
                  </div>
                )}

                {/**********************************
                 * P&L (from grouping)
                 **********************************/}
                {noteKey === "PL" && openSubmenu === mIdx && (
                  <div className={sidemenucss.submenuContainer}>
                    {templateData?.grouping
                      ?.map((note, idx) => ({ note, idx }))
                      .filter(({ note }) => note.category === "PL")
                      .map(({ note, idx }) => (
                        <div
                          key={idx}
                          className={`${sidemenucss.submenuItemNotes} ${
                            activeSubmenu.menuIndex === mIdx &&
                            activeSubmenu.subIndex === idx
                              ? sidemenucss.activeSubMenu
                              : ""
                          }`}
                          onClick={() => {
                            setActiveSubmenu({
                              menuIndex: mIdx,
                              subIndex: idx,
                              subSubIndex: null,
                            });
                            showData("PL", idx, null);
                          }}
                        >
                          {note.name}
                        </div>
                      ))}
                  </div>
                )}

                {/**********************************
                 * GENERIC MENUS (nested subNotes supported)
                 **********************************/}
                {noteKey &&
                  noteKey !== "BS" &&
                  noteKey !== "PL" &&
                  openSubmenu === mIdx && (
                    <div className={sidemenucss.submenuContainer}>
                      {(templateData[noteKey] || []).map((topItem, topIdx) => (
                        <div key={topIdx} style={{ marginBottom: 6 }}>
                          {/* CLICKABLE TOP-LEVEL ITEM (separated from nested area) */}
                          <div
                            className={`${sidemenucss.submenuItemNotes} ${
                              activeSubmenu.menuIndex === mIdx &&
                              activeSubmenu.subIndex === topIdx &&
                              activeSubmenu.subSubIndex === null
                                ? sidemenucss.activeSubMenu
                                : ""
                            }`}
                            onClick={() => {
                              // Selecting top-level item only
                              setActiveSubmenu({
                                menuIndex: mIdx,
                                subIndex: topIdx,
                                subSubIndex: null,
                              });
                              showData(noteKey, topIdx, null);
                            }}
                          >
                            {topItem.name}
                          </div>

                          {/* NESTED SUBNOTES (stopPropagation on click) */}
                          {Array.isArray(topItem.subNotes) && topItem.subNotes.length > 0 && (
                            <div style={{ paddingLeft: 12, marginTop: 4 }}>
                              {topItem.subNotes.map((sn, sIdx) => (
                                <div
                                  key={sIdx}
                                  className={`${sidemenucss.submenuItemNotes} ${
                                    activeSubmenu.menuIndex === mIdx &&
                                    activeSubmenu.subIndex === topIdx &&
                                    activeSubmenu.subSubIndex === sIdx
                                      ? sidemenucss.activeSubMenu
                                      : ""
                                  }`}
                                  style={{ fontSize: 13, paddingLeft: 6 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveSubmenu({
                                      menuIndex: mIdx,
                                      subIndex: topIdx,
                                      subSubIndex: sIdx,
                                    });
                                    showData(noteKey, topIdx, sIdx);
                                  }}
                                >
                                  {sn.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PreviewSidebar;
